import classNames from 'classnames/bind';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';
import { SPACE_ICONS, SPACE_IMAGE_PLACEHOLDER } from 'utils/constants';

import React, { ReactNode } from 'react';

import { Badge, Surface } from '@thinkific/toga-react';

import Image from 'components/Image';

import styles from './SpaceDetailsCard.module.scss';

const cx = classNames.bind(styles);

interface SpaceDetailsCardProps {
  name?: string;
  description?: string;
  icon?: string;
  imageUrl?: string;
  imageAltText?: string;
  isPrivateMember?: boolean;
  className?: string;
  footerAccessory?: ReactNode;
  dataQA?: string;
  showImage?: boolean;
  inheritPrimaryColor?: boolean;
  renderDescriptionAsHTML?: boolean;
}

const SpaceDetailsCard: React.FC<SpaceDetailsCardProps> = ({
  name,
  description,
  icon,
  imageUrl,
  imageAltText,
  isPrivateMember,
  className,
  footerAccessory,
  dataQA,
  showImage = true,
  inheritPrimaryColor = true,
  renderDescriptionAsHTML = false,
}) => {
  const { t } = useTranslation();

  const IconComponent = icon ? SPACE_ICONS[icon] : null;

  return (
    <div className={cx('space-details__container', className)} data-qa={dataQA}>
      <Surface className={cx('space-details__wrapper')}>
        {showImage && (
          <div className={cx('space-details__image')}>
            <Image
              alt={imageAltText || ''}
              height={294}
              src={imageUrl || SPACE_IMAGE_PLACEHOLDER}
              width={524}
            />
          </div>
        )}
        <div className={cx('space-details__body', 'p-2')}>
          <div className={cx('space-details__header')}>
            {IconComponent && (
              <IconComponent
                className={cx({
                  'space-details__icon--inherit': inheritPrimaryColor,
                })}
                data-qa="space-card-icon__image"
              />
            )}
            <h2
              className={cx('space-details__title', 'h3')}
              data-qa="space-card-name__text"
              title={name}
            >
              {name}
            </h2>
          </div>
          {!isPrivateMember && (
            <div
              className={cx('space-details__description', 'mt-2')}
              data-qa="space-card-description__text"
              title={description}
            >
              {renderDescriptionAsHTML ? (
                // eslint-disable-next-line react/no-danger
                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description ?? '') }} />
              ) : (
                description
              )}
            </div>
          )}
          {isPrivateMember && (
            <div>
              <Badge className={cx('space-details__badge')} icon="lock" type="secondary">
                <span>{t('common-Private', 'Private')}</span>
              </Badge>
            </div>
          )}
        </div>
        {!isPrivateMember && footerAccessory && (
          <div className={cx('space-details__footer')}>{footerAccessory}</div>
        )}
      </Surface>
    </div>
  );
};

export default SpaceDetailsCard;
